/**
 * Popover de compte utilisateur réutilisable
 *
 * Affiche les informations utilisateur, le company switcher et les actions
 * (paramètres, page de vente, lien de réservation, déconnexion)
 *
 * @module UserAccountPopover
 * @author Équipe Brydge
 * @since 2.1.0
 */

import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
// @mui
import {alpha, useTheme} from '@mui/material/styles';
import {Box, Stack, Avatar, Divider, Typography, MenuItem, ListItemIcon} from '@mui/material';
// @mui/icons-material
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
// hooks
import {useAuthContext} from 'packages/common/hooks';
import {useAdminAccess} from 'packages/common/hooks/useAdminAccess';
import {useSession, useActiveCompany, usePermissions} from 'packages/common/hooks/session';
import {useRouter} from 'packages/common/routes/hooks';
// components
import Iconify from 'packages/common/components/iconify';
import CompanySwitcher from 'packages/common/components/company-switcher/CompanySwitcher';
import CustomPopover from 'packages/common/components/custom-popover';
// config
import {PUBLIC_URL} from 'apps/dashboard/ConfigGlobal';
import {paths} from 'packages/common/routes/paths';

/**
 * Composant de popover de compte utilisateur
 *
 * @param {Object} popover - Instance du popover (de usePopover)
 * @param {Object} anchorOrigin - Position d'ancrage du popover
 * @param {Object} transformOrigin - Position de transformation du popover
 * @return {JSX.Element} Popover avec informations utilisateur et actions
 */
export default function UserAccountPopover({
  popover,
  anchorOrigin = {vertical: 'top', horizontal: 'left'},
  transformOrigin = {vertical: 'bottom', horizontal: 'left'},
}) {
  const theme = useTheme();
  const router = useRouter();
  const {logout} = useAuthContext();
  const {user} = useSession();
  const {slug, hasStripeAccount, hasMultipleCompanies} = useActiveCompany();
  const {isAdminAccessMode} = useAdminAccess();
  const {hasModuleAccess} = usePermissions();

  const publicUrl = `${PUBLIC_URL}${paths.company.root}/${slug}`;
  const statusColor = hasStripeAccount ? theme.palette.success.main : theme.palette.error.main;

  // Lien de réservation : page publique adressée par slug (parcours sans clé API),
  // alignée sur la page de vente pour ne plus exposer l'ancien widget embarqué.
  const hasRestaurantAccess = hasModuleAccess('restaurant');
  const reservationUrl = `${PUBLIC_URL}${paths.company.root}/${slug}/reservation`;

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
    } catch (error) {
      console.error('Erreur lors de la déconnexion :', error);
    }
  };

  const handleNavigateToSettings = useCallback(() => {
    router.push(paths.app.userAccount);
    popover.onClose();
  }, [router, popover]);

  return (
    <CustomPopover
      hiddenArrow
      open={popover.open}
      onClose={popover.onClose}
      sx={{
        width: 280,
        p: 0,
        bgcolor: (theme) => theme.palette.navy[900],
        backgroundImage: 'none',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(15, 28, 46, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      <Box sx={{p: 2.5}}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            src={user?.photoURL}
            alt={`${user?.firstName} ${user?.lastName}`}
            sx={{
              width: 48,
              height: 48,
              bgcolor: (theme) => theme.palette.primary.main,
              color: 'white',
              fontWeight: 600,
            }}
          >
            {user?.firstName?.charAt(0)?.toUpperCase() || ''}
          </Avatar>
          <Stack sx={{minWidth: 0, flex: 1}}>
            <Typography variant="subtitle2" noWrap sx={{color: '#E2E8F0'}}>
              {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
            </Typography>
            <Typography
              variant="body2"
              sx={{color: 'rgba(226, 232, 240, 0.7)', fontSize: '0.8rem'}}
              noWrap
            >
              {user?.email}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {hasMultipleCompanies && (
        <>
          <Divider sx={{borderColor: 'rgba(255, 255, 255, 0.12)'}} />

          {/* Company Switcher dans le popover */}
          <Box sx={{p: 2}}>
            <CompanySwitcher variant="compact" size="small" />
          </Box>

          <Divider sx={{borderColor: 'rgba(255, 255, 255, 0.12)'}} />
        </>
      )}

      {!hasMultipleCompanies && <Divider sx={{borderColor: 'rgba(255, 255, 255, 0.12)'}} />}

      <Box sx={{p: 1}}>
        {/* Paramètres utilisateur */}
        <MenuItem
          onClick={handleNavigateToSettings}
          sx={{
            'px': 2,
            'py': 1.5,
            'gap': 1.5,
            'borderRadius': 1,
            'color': '#E2E8F0',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{minWidth: 'auto', color: '#E2E8F0'}}>
            <SettingsOutlinedIcon sx={{fontSize: 20}} />
          </ListItemIcon>
          <Typography variant="body2">Paramètres du compte</Typography>
        </MenuItem>

        {/* Lien page de vente */}
        {slug && (
          <MenuItem
            component="a"
            href={publicUrl}
            target="_blank"
            sx={{
              'px': 2,
              'py': 1.5,
              'gap': 1.5,
              'borderRadius': 1,
              'color': '#E2E8F0',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{minWidth: 'auto', position: 'relative', color: '#E2E8F0'}}>
              <Iconify icon="solar:shop-2-linear" width={20} />
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: statusColor,
                  border: `2px solid ${theme.palette.navy[900]}`,
                  boxShadow: `0 0 0 1px ${statusColor}`,
                }}
              />
            </ListItemIcon>
            <Typography variant="body2">Page de vente</Typography>
          </MenuItem>
        )}

        {/* Lien page publique de réservation restaurant */}
        {hasRestaurantAccess && slug && (
          <MenuItem
            component="a"
            href={reservationUrl}
            target="_blank"
            sx={{
              'px': 2,
              'py': 1.5,
              'gap': 1.5,
              'borderRadius': 1,
              'color': '#E2E8F0',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{minWidth: 'auto', color: '#E2E8F0'}}>
              <Iconify icon="solar:calendar-mark-linear" width={20} />
            </ListItemIcon>
            <Typography variant="body2">Lien de réservation</Typography>
          </MenuItem>
        )}

        {slug && <Divider sx={{my: 1, borderColor: 'rgba(255, 255, 255, 0.12)'}} />}

        {/* Déconnexion */}
        <MenuItem
          onClick={handleLogout}
          disabled={isAdminAccessMode}
          sx={{
            'px': 2,
            'py': 1.5,
            'gap': 1.5,
            'borderRadius': 1,
            'color': isAdminAccessMode ? 'rgba(226, 232, 240, 0.4)' : 'error.main',
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
            },
          }}
        >
          <ListItemIcon sx={{minWidth: 'auto'}}>
            <LogoutRoundedIcon
              sx={{
                fontSize: 20,
                color: isAdminAccessMode ? 'rgba(226, 232, 240, 0.4)' : 'error.main',
              }}
            />
          </ListItemIcon>
          <Typography variant="body2">Déconnexion</Typography>
        </MenuItem>
      </Box>
    </CustomPopover>
  );
}

UserAccountPopover.propTypes = {
  popover: PropTypes.object.isRequired,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
};
